import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Stack,
    IconButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

function DataImport() {
    const { api, currentUser } = useAuth();
    const navigate = useNavigate();

    const [ledgers, setLedgers] = useState([]);
    const [selectedLedger, setSelectedLedger] = useState('');
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Protect route strictly
    useEffect(() => {
        if (currentUser && !currentUser.is_super_admin) {
            navigate('/');
        }
    }, [currentUser, navigate]);

    useEffect(() => {
        const fetchLedgers = async () => {
            try {
                const response = await api.get('/ledger/ledgers');
                setLedgers(response.data);
                if (response.data.length > 0) {
                    setSelectedLedger(response.data[0].id);
                }
            } catch (err) {
                setError('Failed to load books');
                console.error(err);
            }
        };

        // Only fetch if super admin
        if (currentUser?.is_super_admin) {
            fetchLedgers();
        }
    }, [api, currentUser]);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setFile(e.target.files[0]);
            setError('');
            setSuccess('');
        }
    };

    const handleUpload = async () => {
        if (!file) {
            setError('Please select an Excel file first');
            return;
        }
        if (!selectedLedger) {
            setError('Please select a book');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        const formData = new FormData();
        formData.append('file', file);
        formData.append('ledger_id', selectedLedger);

        try {
            const response = await api.post('/import/excel', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setSuccess(`Import successful! ${response.data.entries_imported} entries added. Created ${response.data.new_categories_created} missing categories.`);
            setFile(null); // Clear file after success
            // Reset the file input element visually
            const fileInput = document.getElementById('excel-upload');
            if (fileInput) fileInput.value = '';

        } catch (err) {
            console.error('Import failed:', err);
            setError(err.response?.data?.detail || 'Failed to import data');
        } finally {
            setLoading(false);
        }
    };

    // Prevent render flash for unauthorized users
    if (!currentUser?.is_super_admin) {
        return null;
    }

    return (
        <Box sx={{ mt: 4, mb: 4, maxWidth: 600, mx: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => navigate('/admin')} sx={{ mr: 2 }} color="primary">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        Data Import
                    </Typography>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ p: 4 }}>
                <Stack spacing={4}>
                    <Typography variant="body1" color="text.secondary">
                        Upload the "PV Bookkeeping Main" Excel file to populate a book. The tool will parse the "Spending Summary" tab, auto-create missing categories, and insert the transactions.
                    </Typography>

                    {error && <Alert severity="error">{error}</Alert>}
                    {success && <Alert severity="success">{success}</Alert>}

                    <FormControl fullWidth required>
                        <InputLabel id="book-select-label">Target Book</InputLabel>
                        <Select
                            labelId="book-select-label"
                            value={selectedLedger}
                            label="Target Book"
                            onChange={(e) => setSelectedLedger(e.target.value)}
                            disabled={loading}
                        >
                            {ledgers.map(ledger => (
                                <MenuItem key={ledger.id} value={ledger.id}>{ledger.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 1, p: 4, textAlign: 'center', bgcolor: 'primary.50' }}>
                        <input
                            accept=".xlsx,.xls"
                            style={{ display: 'none' }}
                            id="excel-upload"
                            type="file"
                            onChange={handleFileChange}
                            disabled={loading}
                        />
                        <label htmlFor="excel-upload">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<CloudUploadIcon />}
                                size="large"
                                disabled={loading}
                            >
                                Select Excel File
                            </Button>
                        </label>
                        {file && (
                            <Typography variant="body2" sx={{ mt: 2, fontWeight: 500, color: 'primary.main' }}>
                                Selected: {file.name}
                            </Typography>
                        )}
                    </Box>

                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={!file || !selectedLedger || loading}
                        onClick={handleUpload}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                        sx={{ py: 1.5 }}
                    >
                        {loading ? 'Importing...' : 'Upload & Import'}
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
}

export default DataImport;
